import { useEffect, useRef } from "react";
import { storage } from "@/utils/storageManager";
import { useToast } from "@/hooks/use-toast";

interface UseBackgroundToggleHotkeyOptions {
  disabled?: boolean;
}

type BackgroundType = "image" | "solid";

const cycleBackgroundType = (current: BackgroundType): BackgroundType => {
  return current === "image" ? "solid" : "image";
};

export const useBackgroundToggleHotkey = (
  options: UseBackgroundToggleHotkeyOptions = {}
) => {
  const { disabled = false } = options;
  const { toast } = useToast();
  const lastToggleRef = useRef<number>(0);

  useEffect(() => {
    if (disabled) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.ctrlKey || !event.shiftKey) {
        return;
      }

      const key = event.key;
      if (!(key === "I" || key === "i")) {
        return;
      }

      event.preventDefault();

      const now = Date.now();
      if (now - lastToggleRef.current < 600) {
        return;
      }
      lastToggleRef.current = now;

      try {
        const settings = storage.getAppSettings();
        const rawType = settings?.backgroundType as any;
        const currentType: BackgroundType = rawType === 'image' ? 'image' : 'solid';
        const nextType = cycleBackgroundType(currentType);

        const success = storage.updateAppSettings({ backgroundType: nextType });
        if (!success) {
          toast({
            title: "Background unchanged",
            description: "Could not update background preference. Please try again.",
            variant: "destructive",
          });
          return;
        }

        window.dispatchEvent(
          new CustomEvent("appSettingsChanged", {
            detail: { backgroundType: nextType },
          })
        );
      } catch (error) {
        console.error("Failed to cycle background type:", error);
        toast({
          title: "Background unchanged",
          description: "Something went wrong while updating the background.",
          variant: "destructive",
        });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [disabled, toast]);
};
